// src/components/AppVersions/VersionForm.jsx
import { useState } from "react";
import { useModalContext } from "../CustomModal/ModalProvider.jsx";
import { appVersionService } from "../../services/appVersionService";
import { isValidSemver, PLATFORM, CHANNEL, RELEASE_TYPE } from "../../constants/appVersions";
import { InfoTooltip } from "./InfoTooltip";
import {
  FormContainer, Row, FieldRow, Field, Label, LabelRow, ShowInput, StyledSelect, ShowTextarea,
  CheckboxRow, ButtonContainer, StyledButton, HelpText, ProgressBarTrack, ProgressBarFill,
} from "./styles";

const INITIAL = {
  platform: "android",
  channel: "ota",
  release_type: "minor",
  version: "",
  description: "",
  runtime_version: "",
  is_mandatory: true,
  playstore_link: "",
  apple_store_link: "",
};

export function VersionForm({ versionExists, onCreated }) {
  const { showError, showSuccess } = useModalContext();

  const [form, setForm] = useState(INITIAL);
  const [aabFile, setAabFile] = useState(null);
  const [manualFile, setManualFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);

  const isStore = form.channel === "store";

  function setField(name, value) {
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function handleAabFile(e) {
    const file = e.target.files?.[0] || null;
    const expectedExt = form.platform === "ios" ? ".ipa" : ".aab";
    if (file && !file.name.toLowerCase().endsWith(expectedExt)) {
      showError("Arquivo inválido", `Selecione um arquivo ${expectedExt}.`);
      e.target.value = "";
      setAabFile(null);
      return;
    }
    setAabFile(file);
  }

  function handleManualFile(e) {
    const file = e.target.files?.[0] || null;
    if (file && !file.name.toLowerCase().endsWith(".pdf")) {
      showError("Arquivo inválido", "O manual deve ser um PDF.");
      e.target.value = "";
      setManualFile(null);
      return;
    }
    setManualFile(file);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (submitting) return;

    if (!isValidSemver(form.version)) {
      showError("Versão inválida", "Use o formato semver, ex: 1.3.5.");
      return;
    }
    if (versionExists(form.platform, form.version)) {
      showError("Versão duplicada", `Já existe ${form.version} para ${form.platform}.`);
      return;
    }
    if (!form.description.trim()) {
      showError("Descrição obrigatória", "Preencha as release notes.");
      return;
    }
    if (!form.runtime_version.trim()) {
      showError("Runtime obrigatório", "Cole o runtime_version do build EAS.");
      return;
    }
    if (isStore && !aabFile) {
      showError("AAB obrigatório", "Versão de store exige o arquivo .aab.");
      return;
    }
    if (!isStore && aabFile) {
      showError("AAB não permitido", "Versão OTA não pode ter arquivo .aab.");
      return;
    }

    setSubmitting(true);
    const hasFile = Boolean(aabFile || manualFile);
    if (hasFile) setUploadProgress(0);

    try {
      const payload = {
        platform: form.platform,
        version: form.version,
        release_type: form.release_type,
        channel: form.channel,
        description: form.description,
        runtime_version: form.runtime_version,
        is_mandatory: form.is_mandatory,
        ...(form.playstore_link.trim() ? { playstore_link: form.playstore_link.trim() } : {}),
        ...(form.apple_store_link.trim() ? { apple_store_link: form.apple_store_link.trim() } : {}),
      };

      const files = {
        ...(aabFile ? { binary_file: aabFile } : {}),
        ...(manualFile ? { manual_pdf_file: manualFile } : {}),
      };

      await appVersionService.createVersion(
        payload,
        files,
        hasFile ? setUploadProgress : undefined
      );

      showSuccess("Sucesso!", `Versão ${form.version} criada.`);
      setForm(INITIAL);
      setAabFile(null);
      setManualFile(null);
      onCreated?.();
    } catch (err) {
      const data = err?.response?.data;
      const firstMsg =
        data && typeof data === "object"
          ? Object.values(data)?.[0]?.[0] || "Erro ao criar versão."
          : "Erro ao criar versão.";
      showError("Erro!", firstMsg);
    } finally {
      setSubmitting(false);
      setUploadProgress(null);
    }
  }

  return (
    <FormContainer onSubmit={handleSubmit}>
      <FieldRow>
        <Field>
          <Label>Plataforma</Label>
          <StyledSelect value={form.platform} onChange={(e) => setField("platform", e.target.value)}>
            {PLATFORM.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </StyledSelect>
        </Field>

        <Field>
          <LabelRow>
            <Label>Canal</Label>
            <InfoTooltip
              position="right"
              text={`OTA (Over-The-Air): atualização enviada direto pelo Expo/EAS Update, sem passar pela loja.
                O app baixa e aplica sozinho na próxima abertura.

                Store: exige publicação na Play Store / App Store.
                O usuário atualiza manualmente pela loja.`}
            />
          </LabelRow>
          <StyledSelect
            value={form.channel}
            onChange={(e) => {
              setField("channel", e.target.value);
              if (e.target.value !== "store") setAabFile(null);
            }}
          >
            {CHANNEL.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </StyledSelect>
        </Field>
      </FieldRow>

      <FieldRow>
        <Field>
          <LabelRow>
            <Label>Tipo de release</Label>
            <InfoTooltip
              position="right"
              text={`Classifica o impacto da mudança (semver):

            Major — mudança grande, quebra compatibilidade.
            Ex.: reformular o fluxo de login, invalidando sessões antigas (1.4.2 → 2.0.0).

            Minor — nova funcionalidade compatível, não quebra nada.
            Ex.: adicionar a tela de força de atualização (1.0.0 → 1.1.0).

            Patch — correção de bug, sem mudar o nativo.
            Ex.: corrigir mensagem de erro cortada na leitura do QR Code (1.1.0 → 1.1.1).`}
            />
          </LabelRow>
          <StyledSelect value={form.release_type} onChange={(e) => setField("release_type", e.target.value)}>
            {RELEASE_TYPE.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </StyledSelect>
        </Field>

        <Field style={{ maxWidth: "50%" }}>
          <Label>Versão</Label>
          <ShowInput
            type="text"
            placeholder="1.3.5"
            value={form.version}
            onChange={(e) => setField("version", e.target.value)}
          />
        </Field>
      </FieldRow>

      <Row>
        <Label>Descrição (release notes)</Label>
        <ShowTextarea
          value={form.description}
          onChange={(e) => setField("description", e.target.value)}
          placeholder="O que mudou nesta versão"
        />
      </Row>

      <Row>
        <LabelRow>
          <Label>Runtime version</Label>
          <InfoTooltip
            position="right"
            text={`Runtime version é a impressão digital da camada nativa do app (dependências nativas + configuração do build). Só muda quando algo nativo muda.

            Atualizações OTA só se aplicam a versões com o mesmo runtime_version — se ele mudou, o usuário só recebe a atualização pela loja.

            Copie esse valor do output gerado pelo comando de build do EAS (eas build).`}
          />
        </LabelRow>
        <ShowInput
          type="text"
          placeholder="cole o hash/valor do output do EAS build"
          value={form.runtime_version}
          onChange={(e) => setField("runtime_version", e.target.value)}
        />
        <HelpText>Agrupa a compatibilidade de OTA. Leia do output do build.</HelpText>
      </Row>

      {isStore && (
        <Row>
          <Label>{form.platform === "ios" ? "Arquivo IPA" : "Arquivo AAB"}</Label>
          <input
            type="file"
            accept={form.platform === "ios" ? ".ipa" : ".aab"}
            onChange={handleAabFile}
          />
          {aabFile && <HelpText>Selecionado: {aabFile.name}</HelpText>}
        </Row>
      )}

      <Row>
        <Label>Manual em PDF (opcional)</Label>
        <input type="file" accept=".pdf" onChange={handleManualFile} />
        {manualFile && <HelpText>Selecionado: {manualFile.name}</HelpText>}
      </Row>

      <Row>
        <Label>
          {form.platform === "ios" ? "Link App Store (opcional)" : "Link Play Store (opcional)"}
        </Label>
        <ShowInput
          type="url"
          placeholder={
            form.platform === "ios"
              ? "https://apps.apple.com/..."
              : "https://play.google.com/store/apps/details?id=..."
          }
          value={form.platform === "ios" ? form.apple_store_link : form.playstore_link}
          onChange={(e) => {
            const field = form.platform === "ios" ? "apple_store_link" : "playstore_link";
            setField(field, e.target.value);
          }}
        />
      </Row>

      <Row>
        <CheckboxRow>
          <input
            type="checkbox"
            checked={form.is_mandatory}
            onChange={(e) => setField("is_mandatory", e.target.checked)}
          />
          <span>Atualização obrigatória</span>
        </CheckboxRow>
      </Row>

      {uploadProgress !== null && (
        <Row>
          <HelpText>Enviando arquivo... {uploadProgress}%</HelpText>
          <ProgressBarTrack>
            <ProgressBarFill $percent={uploadProgress} />
          </ProgressBarTrack>
        </Row>
      )}

      <ButtonContainer>
        <StyledButton type="submit" disabled={submitting}>
          {submitting ? "Salvando..." : "Criar versão"}
        </StyledButton>
      </ButtonContainer>
    </FormContainer>
  );
}